import { useEventListener } from '@vueuse/core';
import useSvgViewBox from '~/Components/Common/Eventmap/Composables/useSvgViewBox';
import usePointerEvents from '~/Components/Common/Eventmap/Composables/usePointerEvents';
import useTouchEvents from '~/Components/Common/Eventmap/Composables/useTouchEvents';

export default function useEventmap(params) {
  const {
    props, emit, refs, containerWidth, containerHeight,
  } = params;

  // Destructure references for easier access
  const {
    svg,
    zoomInstructions,
    selectedLocationEl,
    selectedPathEl,
    topHandle,
    rightHandle,
    bottomHandle,
    leftHandle,
    pathHandle1,
    pathHandle2,
    isPanning,
    isDragging,
    isPolyPointMoving,
    svgX,
    svgY,
    isPinching,
    isResizing,
    isPathNodeMoving,
  } = refs;

  // ViewBox management: Handles the SVG viewBox adjustments based on container dimensions and event map properties.
  const svgViewBox = useSvgViewBox({
    svg,
    containerWidth,
    containerHeight,
    eventmap: props.eventmap,
  });

  // Pointer events management: Manages pointer interactions such as dragging, panning, and resizing.
  const pointerHandler = usePointerEvents({
    svg,
    zoomInstructions,
    selectedLocationEl,
    selectedPathEl,
    topHandle,
    rightHandle,
    bottomHandle,
    leftHandle,
    pathHandle1,
    pathHandle2,
    isPanning,
    isDragging,
    isPolyPointMoving,
    svgX,
    svgY,
    props,
    emit,
    svgViewBox,
    isPinching,
    isResizing,
    isPathNodeMoving,
  });

  // Touch events management: Handles touch interactions including pinching.
  const touchHandler = useTouchEvents({
    svg,
    props,
    emit,
    svgViewBox,
    eventmap: props.eventmap,
    containerWidth,
    containerHeight,
    isPanning,
    isPinching,
    isResizing,
    isPathNodeMoving,
    isPolyPointMoving,
    isDragging,
  });

  const { screenToSvgCoordinates } = svgViewBox;

  const { initializePointerListeners } = pointerHandler;

  const { initializeTouchListeners } = touchHandler;

  /**
   * Initialize all event listeners for the SVG element.
   * This includes pointer events, touch events, and context menu interactions.
   */
  const initializeEventListeners = () => {
    // Initialize pointer event listeners: Sets up event listeners for pointer interactions.
    initializePointerListeners();

    // Initialize touch event listeners: Sets up event listeners for touch interactions.
    initializeTouchListeners();

    // Add a context menu listener for right-click actions: Prevents default context menu and emits custom event.
    useEventListener(svg, 'contextmenu', (event) => {
      event.preventDefault();
      const { x, y } = screenToSvgCoordinates(event.clientX, event.clientY);
      emit('contentMenuOpened', {
        x, y, offsetX: event.offsetX, offsetY: event.offsetY,
      });
    });
  };

  // Return all necessary functions and properties for managing the event map.
  return {
    ...svgViewBox,
    ...pointerHandler,
    initializeEventListeners,
  };
}
